/**
 * ----------------------------------------------------------------------------------------------------------------------
 * BUNDLE BUILDER
 * ----------------------------------------------------------------------------------------------------------------------
 *
 * Powers the "fixed kit" bundle section. The customer configures each component (choosing only the options you allow),
 * and on submit the bundle is added to the cart as ITS OWN single product line (priced at this product's price), with
 * every selection saved as a readable line-item property for fulfilment — e.g.:
 *
 *   "Legend Goalkeeper Glove #1": "Inferno, Size: 9"
 *   "Onesport Aura Plus Football": "Size: 5"
 *
 * No discount function is needed: the price IS the bundle product's price. It reuses the theme's cart-refresh events so
 * the drawer opens/updates like any other add-to-cart.
 */

(function () {
  if (window.customElements.get('bundle-form')) {
    return;
  }

  // Canonical Shopify money formatter (supports {{amount}}, {{amount_no_decimals}}, comma-separator variants).
  function formatMoney(cents, format) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }

    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function defaultOption(opt, def) {
      return typeof opt === 'undefined' ? def : opt;
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal = defaultOption(decimal, '.');

      if (isNaN(number) || number == null) {
        return 0;
      }

      number = (number / 100.0).toFixed(precision);

      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? decimal + parts[1] : '';

      return dollarsAmount + centsAmount;
    }

    let value = '';
    const match = format.match(placeholderRegex);
    const formatString = match ? match[1] : 'amount';

    switch (formatString) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ', '');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
    }

    return format.replace(placeholderRegex, value);
  }

  class BundleItem extends HTMLElement {}

  class BundleForm extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('bundle-item'));
      this.button = this.querySelector('[data-bundle-submit]');
      this.errorEl = this.querySelector('[data-bundle-error]');
      this.unavailableEl = this.querySelector('[data-bundle-unavailable]');
      this.savingsEl = this.querySelector('[data-bundle-savings]');
      this.compareEl = this.querySelector('[data-bundle-compare]');

      this.bundleVariantId = this.dataset.bundleVariantId || '';
      this.bundlePrice = parseInt(this.dataset.bundlePrice || '0', 10); // in cents — this product's own price
      this.bundleAvailable = this.dataset.bundleAvailable === 'true';
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.bundleTitle = this.dataset.bundleTitle || '';

      this.items.forEach((item) => {
        const configScript = item.querySelector('[data-config]');
        try {
          item._config = JSON.parse(configScript.textContent);
        } catch (e) {
          item._config = { label: '', quantity: 1, choices: [] };
        }
      });

      this.addEventListener('change', () => this.refresh());
      if (this.button) {
        this.button.addEventListener('click', (event) => this.onSubmit(event));
      }

      this.refresh();
    }

    /** The index of the currently selected choice (e.g. colour). Single-choice components are always choice 0. */
    activeChoiceIndex(item) {
      if (item._config.choices.length === 1) {
        return 0;
      }
      const checked = item.querySelector('input[data-choice]:checked');
      return checked ? parseInt(checked.value, 10) : null;
    }

    /**
     * Resolve one component to { complete, choiceIndex, target, variant } from the current selections + presets.
     * `target` maps option position -> chosen value; `variant` (if found) carries the component's price.
     */
    resolveItem(item) {
      const choiceIndex = this.activeChoiceIndex(item);
      if (choiceIndex == null) {
        return { complete: false, choiceIndex: null, target: {}, variant: null };
      }

      const choice = item._config.choices[choiceIndex];
      const target = {};
      let complete = true;

      choice.options.forEach((option) => {
        if (option.mode === 'preset') {
          target[option.position] = option.value;
        } else {
          const checked = item.querySelector(
            '[data-choice-panel="' + choiceIndex + '"] input[data-position="' + option.position + '"]:checked'
          );
          if (checked) {
            target[option.position] = checked.value;
          } else {
            complete = false;
          }
        }
      });

      let variant = null;
      if (complete) {
        variant = choice.variants.find((candidate) =>
          choice.options.every((option) => candidate.options[option.position - 1] === target[option.position])
        );
      }

      return { complete: complete, choiceIndex: choiceIndex, target: target, variant: variant };
    }

    /** Show the option panel for the active choice, hide the rest, and sync the choice summary label. */
    updateChoicePanels(item) {
      const config = item._config;
      if (config.choices.length === 1) {
        return;
      }
      const choiceIndex = this.activeChoiceIndex(item);

      item.querySelectorAll('[data-choice-panel]').forEach((panel) => {
        panel.hidden = parseInt(panel.getAttribute('data-choice-panel'), 10) !== choiceIndex;
      });

      const selectedLabel = item.querySelector('[data-choice-selected]');
      if (selectedLabel && choiceIndex != null) {
        selectedLabel.textContent = config.choices[choiceIndex].label;
      }
    }

    /** Swap the component thumbnail to the active choice's product image (e.g. black -> white glove). */
    updateImage(item) {
      const config = item._config;
      if (config.choices.length === 1) {
        return;
      }
      const choiceIndex = this.activeChoiceIndex(item);
      if (choiceIndex == null) {
        return;
      }
      const image = config.choices[choiceIndex].image;
      const img = item.querySelector('.bundle-item__image');
      if (img && image && img.src !== image) {
        img.removeAttribute('srcset');
        img.src = image;
      }
    }

    /** A human-readable summary of one component's selection, saved as a line-item property for fulfilment. */
    describeSelection(item, result) {
      const config = item._config;
      const choice = config.choices[result.choiceIndex];
      const parts = [];

      if (config.choices.length > 1) {
        parts.push(choice.label);
      }
      choice.options.forEach((option) => {
        const value = option.mode === 'preset' ? option.value : result.target[option.position];
        parts.push(option.name + ': ' + value);
      });

      let value = parts.join(', ');
      const quantity = parseInt(config.quantity || 1, 10);
      if (quantity > 1) {
        value += ' ×' + quantity;
      }
      return value;
    }

    refresh() {
      let subtotal = 0;
      let allComplete = true;

      this.items.forEach((item) => {
        this.updateChoicePanels(item);
        this.updateImage(item);

        const result = this.resolveItem(item);
        const quantity = parseInt(item._config.quantity || 1, 10);

        // Keep each "Size: M" style summary label in sync with its own panel's selection.
        item.querySelectorAll('[data-selected-value]').forEach((el) => {
          const position = el.getAttribute('data-selected-value');
          const panel = el.closest('[data-choice-panel]') || item;
          const checked = panel.querySelector('input[data-position="' + position + '"]:checked');
          el.textContent = checked ? checked.value : '';
        });

        if (!result.complete) {
          allComplete = false;
        }
        if (result.variant) {
          subtotal += result.variant.price * quantity;
        }
      });

      const purchasable = allComplete && this.bundleAvailable;

      if (this.button) {
        this.button.disabled = !purchasable;
      }
      if (this.unavailableEl) {
        this.unavailableEl.hidden = this.bundleAvailable; // shown only when the bundle product is sold out
      }
      if (this.errorEl) {
        this.errorEl.hidden = true;
      }

      this.updateSavings(subtotal, allComplete);
    }

    updateSavings(subtotal, resolved) {
      if (!this.savingsEl && !this.compareEl) {
        return;
      }

      const savings = subtotal - this.bundlePrice;
      const show = resolved && savings > 0;

      if (this.compareEl) {
        this.compareEl.hidden = !show;
        if (show) {
          this.compareEl.textContent = formatMoney(subtotal, this.moneyFormat);
        }
      }
      if (this.savingsEl) {
        this.savingsEl.hidden = !show;
        if (show) {
          const template = this.savingsEl.getAttribute('data-savings-template') || 'Save [amount]';
          this.savingsEl.textContent = template.replace('[amount]', formatMoney(savings, this.moneyFormat));
        }
      }
    }

    async onSubmit(event) {
      event.preventDefault();

      if (this.button && (this.button.disabled || this.button.getAttribute('aria-busy') === 'true')) {
        return;
      }
      if (!this.bundleVariantId) {
        this.showError();
        return;
      }

      const properties = {};
      let valid = true;

      this.items.forEach((item) => {
        const result = this.resolveItem(item);
        if (!result.complete) {
          valid = false;
          return;
        }
        const baseKey = item._config.label || 'Item';
        let key = baseKey;
        let n = 2;
        while (Object.prototype.hasOwnProperty.call(properties, key)) {
          key = baseKey + ' (' + n++ + ')';
        }
        properties[key] = this.describeSelection(item, result);
      });

      if (!valid) {
        this.refresh();
        return;
      }

      if (this.button) {
        this.button.setAttribute('aria-busy', 'true');
      }
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      // Collect the sections the cart wants re-rendered (the cart drawer registers itself here), same as ProductForm.
      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      let response;
      try {
        response = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items: [{ id: this.bundleVariantId, quantity: 1, properties: properties }],
            sections: sectionsToBundle.join(','),
            sections_url: window.location.pathname,
          }),
        });
      } catch (e) {
        response = null;
      }

      if (this.button) {
        this.button.removeAttribute('aria-busy');
      }
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));

      if (!response) {
        this.showError();
        return;
      }

      const responseJson = await response.json();

      if (response.ok) {
        if (
          window.themeVariables &&
          (window.themeVariables.settings.cartType === 'page' || window.themeVariables.settings.pageType === 'cart')
        ) {
          window.location.href = `${Shopify.routes.root}cart`;
          return;
        }

        const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        cartContent['sections'] = responseJson['sections'];

        this.dispatchEvent(
          new CustomEvent('variant:add', {
            bubbles: true,
            detail: {
              items: responseJson.hasOwnProperty('items') ? responseJson['items'] : [responseJson],
              cart: cartContent,
              onSuccessDo: 'force_open_drawer',
            },
          })
        );
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', {
            bubbles: true,
            detail: {
              baseEvent: 'variant:add',
              onSuccessDo: 'force_open_drawer',
              cart: cartContent,
            },
          })
        );
      } else {
        this.showError(responseJson && responseJson['description']);
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    }

    showError(message) {
      if (!this.errorEl) {
        return;
      }
      if (message) {
        this.errorEl.textContent = message;
      }
      this.errorEl.hidden = false;
    }
  }

  window.customElements.define('bundle-item', BundleItem);
  window.customElements.define('bundle-form', BundleForm);
})();
